import React, { useState, useEffect } from 'react';
import './NouvellesInscriptions.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';
import api from '../../services/api';

function NouvellesInscriptions() {
  const [inscriptions, setInscriptions] = useState([]);
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [motifRefus, setMotifRefus] = useState('');
  const [classe, setClasse] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    en_attente: 0,
    acceptees: 0,
    refusees: 0
  });

  useEffect(() => {
    fetchInscriptions();
    fetchStatistiques();
  }, []);

  const fetchInscriptions = async () => {
    try {
      const response = await api.get('/admin/inscriptions');
      const data = response.data || {};
      const list = data.data || (Array.isArray(data) ? data : []);
      setInscriptions(list);
    } catch (error) {
      console.error('Erreur lors du chargement des inscriptions:', error);
      setInscriptions([]);
    }
  };

  const fetchStatistiques = async () => {
    try {
      const response = await api.get('/admin/inscriptions/statistiques');
      const data = response.data || {};
      setStats({
        total: data.total || 0,
        en_attente: data.en_attente || 0,
        acceptees: data.acceptees || 0,
        refusees: data.refusees || 0
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const handleViewDetails = (inscription) => {
    setSelectedInscription(inscription);
    setClasse(inscription.niveau_etude);
    setShowModal(true);
  };

  const handleAccepter = async () => {
    if (!classe) {
      alert('⚠️ Veuillez sélectionner une classe');
      return;
    }

    try {
      const response = await api.post(`/admin/inscriptions/${selectedInscription.id}/accepter`, { classe });
      const data = response.data || {};

      if (response.status === 200) {
        alert(`✅ Inscription acceptée!\n\nIdentifiants envoyés à: ${selectedInscription.email}\nMatricule: ${data.matricule || 'ETU2024ABC123'}\nMot de passe: ${data.mot_de_passe || 'temp123456'}`);
        setShowModal(false);
        setSelectedInscription(null);
        setClasse('');
        fetchInscriptions();
        fetchStatistiques();
      } else {
        alert(`❌ ${data.message || 'Erreur lors de l\'acceptation'}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'acceptation:', error);
      const errMsg = error.response?.data?.message || 'Erreur lors de l\'acceptation de l\'inscription';
      alert(`❌ ${errMsg}`);
    }
  };

  const handleRejeter = async () => {
    if (!motifRefus.trim()) {
      alert('⚠️ Veuillez indiquer un motif de refus');
      return;
    }

    try {
      const response = await api.post(`/admin/inscriptions/${selectedInscription.id}/rejeter`, { motif_refus: motifRefus });
      const data = response.data || {};

      if (response.status === 200) {
        alert('✅ Inscription rejetée');
        setShowRejectModal(false);
        setShowModal(false);
        setSelectedInscription(null);
        setMotifRefus('');
        fetchInscriptions();
        fetchStatistiques();
      } else {
        alert(`❌ ${data.message || 'Erreur lors du rejet'}`);
      }
    } catch (error) {
      console.error('Erreur lors du rejet:', error);
      const errMsg = error.response?.data?.message || 'Erreur lors du rejet de l\'inscription';
      alert(`❌ ${errMsg}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Nouvelles Inscriptions" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="inscriptions-content">

            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Total Inscriptions</span>
                </div>
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">inscriptions reçues</div>
              </div>

              <div className="stat-card pending">
                <div className="stat-header">
                  <span className="stat-title">En Attente</span>
                </div>
                <div className="stat-value">{stats.en_attente}</div>
                <div className="stat-label">à traiter</div>
              </div>

              <div className="stat-card accepted">
                <div className="stat-header">
                  <span className="stat-title">Acceptées</span>
                </div>
                <div className="stat-value">{stats.acceptees}</div>
                <div className="stat-label">inscriptions validées</div>
              </div>

              <div className="stat-card rejected">
                <div className="stat-header">
                  <span className="stat-title">Rejetées</span>
                </div>
                <div className="stat-value">{stats.refusees}</div>
                <div className="stat-label">inscriptions refusées</div>
              </div>
            </div>

            {/* Inscriptions List */}
            <div className="inscriptions-table-card">
              <div className="table-header">
                <h2>Inscriptions en attente</h2>
                <div className="table-actions">
                  <input 
                    type="text" 
                    placeholder="Rechercher..." 
                    className="search-input"
                  />
                </div>
              </div>

              <table className="inscriptions-table">
                <thead>
                  <tr>
                    <th>Nom Complet</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Filière</th>
                    <th>Niveau</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inscriptions.map((inscription) => (
                    <tr key={inscription.id}>
                      <td>
                        <div className="student-info">
                          <div className="student-avatar">
                            {inscription.prenom[0]}{inscription.nom[0]}
                          </div>
                          <span>{inscription.prenom} {inscription.nom}</span>
                        </div>
                      </td>
                      <td>{inscription.email}</td>
                      <td>{inscription.telephone}</td>
                      <td>
                        <span className="filiere-badge">
                          {inscription.filiere ? inscription.filiere.nom : 'Non renseigné'}
                        </span>
                      </td>
                      <td>{inscription.niveau_etude}</td>
                      <td>{formatDate(inscription.created_at)}</td>
                      <td>
                        <button 
                          className="btn-view"
                          onClick={() => handleViewDetails(inscription)}
                        >
                          Voir détails
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {inscriptions.length === 0 && (
                <div className="empty-state">
                  <p>Aucune inscription en attente</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Modal Détails */}
      {showModal && selectedInscription && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Détails de l'inscription</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>Informations du tuteur</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Nom du tuteur</label>
                    <p>{selectedInscription.nom_tuteur || 'Non renseigné'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Téléphone du tuteur</label>
                    <p>{selectedInscription.telephone_tuteur || 'Non renseigné'}</p>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Informations personnelles</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Nom complet</label>
                    <p>{selectedInscription.prenom} {selectedInscription.nom}</p>
                  </div>
                  <div className="detail-item">
                    <label>Genre</label>
                    <p>{selectedInscription.genre || 'Non renseigné'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Date de naissance</label>
                    <p>{formatDate(selectedInscription.date_naissance)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Lieu de naissance</label>
                    <p>{selectedInscription.lieu_naissance}</p>
                  </div>
                  <div className="detail-item full-width">
                    <label>Adresse</label>
                    <p>{selectedInscription.adresse}</p>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Contact</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Email</label>
                    <p>{selectedInscription.email}</p>
                  </div>
                  <div className="detail-item">
                    <label>Téléphone</label>
                    <p>{selectedInscription.telephone}</p>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Informations académiques</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Filière</label>
                    <p>{selectedInscription.filiere ? selectedInscription.filiere.nom : 'Non renseigné'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Niveau d'étude</label>
                    <p>{selectedInscription.niveau_etude}</p>
                  </div>
                  <div className="detail-item">
                    <label>Établissement actuel</label>
                    <p>{selectedInscription.etablissement_actuel || 'Non renseigné'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Moyenne générale</label>
                    <p>{selectedInscription.moyenne_generale || 'Non renseigné'}</p>
                  </div>
                  <div className="detail-item full-width">
                    <label>Activités extrascolaires</label>
                    <p>{selectedInscription.activites_extrascolaires || 'Non renseigné'}</p>
                  </div>
                  <div className="detail-item full-width">
                    <label>Langues étrangères</label>
                    <p>{selectedInscription.langues_etrangeres || 'Non renseigné'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Assigner à la classe</label>
                    <input 
                      type="text" 
                      value={classe}
                      onChange={(e) => setClasse(e.target.value)}
                      placeholder="Ex: Licence 1 - Groupe A"
                      className="classe-input"
                    />
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Documents fournis</h3>
                <div className="documents-grid">
                  {selectedInscription.documents?.photo_identite && (
                    <div className="document-item">
                      <div className="document-icon">📷</div>
                      <div className="document-info">
                        <div className="document-name">Photo d'identité</div>
                        <a 
                          href={`http://127.0.0.1:8000/storage/${selectedInscription.documents.photo_identite}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-link"
                        >
                          Voir le document
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {selectedInscription.documents?.certificat_naissance && (
                    <div className="document-item">
                      <div className="document-icon">📄</div>
                      <div className="document-info">
                        <div className="document-name">Certificat de naissance</div>
                        <a 
                          href={`http://127.0.0.1:8000/storage/${selectedInscription.documents.certificat_naissance}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-link"
                        >
                          Télécharger PDF
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {selectedInscription.documents?.bulletin_notes && (
                    <div className="document-item">
                      <div className="document-icon">📊</div>
                      <div className="document-info">
                        <div className="document-name">Bulletin de notes</div>
                        <a 
                          href={`http://127.0.0.1:8000/storage/${selectedInscription.documents.bulletin_notes}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-link"
                        >
                          Télécharger PDF
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {selectedInscription.documents?.certificat_scolarite && (
                    <div className="document-item">
                      <div className="document-icon">🎓</div>
                      <div className="document-info">
                        <div className="document-name">Certificat de scolarité</div>
                        <a 
                          href={`http://127.0.0.1:8000/storage/${selectedInscription.documents.certificat_scolarite}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-link"
                        >
                          Télécharger PDF
                        </a>
                      </div>
                    </div>
                  )}

                  {(!selectedInscription.documents || Object.keys(selectedInscription.documents).length === 0) && (
                    <div className="no-documents">
                      <p>Aucun document fourni</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-reject"
                onClick={() => setShowRejectModal(true)}
              >
                Rejeter
              </button>
              <button 
                className="btn-accept"
                onClick={handleAccepter}
              >
                Accepter l'inscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rejet */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rejeter l'inscription</h2>
              <button className="close-btn" onClick={() => setShowRejectModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <label>Motif du rejet (optionnel)</label>
              <textarea
                value={motifRefus}
                onChange={(e) => setMotifRefus(e.target.value)}
                placeholder="Expliquez la raison du rejet..."
                rows="4"
                className="motif-textarea"
              />
            </div>

            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowRejectModal(false)}
              >
                Annuler
              </button>
              <button 
                className="btn-reject"
                onClick={handleRejeter}
              >
                Confirmer le rejet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NouvellesInscriptions;
